import {
	CATEGORY_ORDER,
	PERMIT_SORT_FIELDS,
	type PermitSortField,
	type PermitSortOrder,
	type PermitStatus,
} from '@redstack/shared'
import {createFileRoute, stripSearchParams} from '@tanstack/react-router'
import {zodValidator} from '@tanstack/zod-adapter'
import {z} from 'zod'
import {BoardPage} from '@/features/board/BoardPage'

export type BoardView = 'board' | 'list'

export const defaultBoardSearch = {
	view: 'board' as BoardView,
	regions: [] as string[],
	assignedTo: [] as string[],
	search: undefined as string | undefined,
	sprintId: undefined as string | undefined,
	status: [] as PermitStatus[],
	page: 1,
	pageSize: 50,
	contractorKey: undefined as string | undefined,
	contractorLabel: undefined as string | undefined,
	sortBy: undefined as PermitSortField | undefined,
	sortOrder: undefined as PermitSortOrder | undefined,
}

export function getDefaultBoardSearch() {
	return {...defaultBoardSearch}
}

export const boardSearchSchema = z.object({
	view: z.enum(['board', 'list']).default('board'),
	regions: z.array(z.string()).default([]),
	assignedTo: z.array(z.string()).default([]),
	search: z.string().optional(),
	sprintId: z.string().optional(),
	status: z.array(z.enum(CATEGORY_ORDER)).default([]),
	page: z.number().min(1).default(1),
	pageSize: z.number().min(1).max(500).default(50),
	contractorKey: z.string().optional(),
	contractorLabel: z.string().optional(),
	sortBy: z.enum(PERMIT_SORT_FIELDS).optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
})

export type BoardSearch = z.output<typeof boardSearchSchema>

function BoardRoute() {
	return <BoardPage />
}

export const Route = createFileRoute('/board/')({
	component: BoardRoute,
	validateSearch: zodValidator(boardSearchSchema),
	search: {
		middlewares: [
			stripSearchParams(defaultBoardSearch),
			// Drops `issueDateFrom` / `issueDateTo` from legacy URLs. The free
			// issue-date filter was removed (permits are scoped by cycle now), but
			// deleting the keys from the schema only removes them from the *type* —
			// not from the URL. The router is not in strict mode, so validated
			// search is merged onto the raw `location.search`, and every
			// `search: prev => ({...prev, …})` setter would re-emit a bookmarked
			// `?issueDateFrom=…` forever.
			//
			// `stripSearchParams` can't do this: its object form only strips keys
			// whose value equals a default, and its array form is typed to `keyof`
			// the schema — which these keys deliberately no longer are. Inlined so
			// the middleware array supplies the parameter types contextually.
			// Safe to delete once old links have aged out (~2 releases).
			({search, next}) => {
				const result = {...next(search)}
				delete (result as Record<string, unknown>).issueDateFrom
				delete (result as Record<string, unknown>).issueDateTo
				return result
			},
		],
	},
})
